import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Dock layout + preferences (local, per-device). The widgets themselves live in
 * the app-sdk dock-widget registry; this store only tracks which always-available
 * widgets are on, their order, which desktop panels are open, and the mobile page.
 */
/** Where the desktop dock sits. "appbar" docks it into the top bar instead. */
export type DockPosition =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left"
  | "appbar";

export const DOCK_POSITIONS: DockPosition[] = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
  "appbar",
];

export const DOCK_POSITION_LABELS: Record<DockPosition, string> = {
  "bottom-right": "Bottom right",
  "bottom-left": "Bottom left",
  "top-right": "Top right",
  "top-left": "Top left",
  appbar: "In the app bar",
};

interface DockState {
  /** widgetId → on/off for non-contextual (always-available) widgets. Default on. */
  enabled: Record<string, boolean>;
  /** Display order of non-contextual widgets (unknown ids fall to the end). */
  order: string[];
  /**
   * Desktop: the open floating panel. Kept as an array for call-site
   * compatibility, but only ever holds one id — stacked panels covered the
   * screen and made the dock feel like clutter.
   */
  openPanels: string[];
  /** Mobile: active swipe page index into the visible-widget list. */
  mobileIndex: number;
  /** Desktop: where the launcher sits right now. */
  position: DockPosition;
  /** Remember a position per app and restore it on app switch. */
  smartPosition: boolean;
  /** appId → the position last used while that app was open. */
  appPositions: Record<string, DockPosition>;

  setEnabled: (id: string, on: boolean) => void;
  reorder: (ids: string[]) => void;
  togglePanel: (id: string) => void;
  closePanel: (id: string) => void;
  setMobileIndex: (i: number) => void;
  /** Move the dock. When smart mode is on, remembers it for `appId`. */
  setPosition: (position: DockPosition, appId?: string | null) => void;
  setSmartPosition: (on: boolean) => void;
  /** Called on app switch — adopts that app's remembered spot. */
  applyAppPosition: (appId: string | null) => void;
}

export const useDockStore = create<DockState>()(
  persist(
    (set) => ({
      enabled: {},
      order: [],
      openPanels: [],
      mobileIndex: 0,
      position: "bottom-right",
      smartPosition: false,
      appPositions: {},

      setEnabled: (id, on) =>
        set((s) => ({ enabled: { ...s.enabled, [id]: on } })),

      reorder: (ids) => set({ order: ids }),

      togglePanel: (id) =>
        set((s) => ({
          openPanels: s.openPanels.includes(id) ? [] : [id],
        })),

      closePanel: (id) =>
        set((s) => ({ openPanels: s.openPanels.filter((x) => x !== id) })),

      setMobileIndex: (i) => set({ mobileIndex: i }),

      setPosition: (position, appId) =>
        set((s) => ({
          position,
          appPositions:
            s.smartPosition && appId
              ? { ...s.appPositions, [appId]: position }
              : s.appPositions,
        })),

      setSmartPosition: (smartPosition) => set({ smartPosition }),

      applyAppPosition: (appId) =>
        set((s) => {
          if (!s.smartPosition || !appId) return s;
          const remembered = s.appPositions[appId];
          if (!remembered || remembered === s.position) return s;
          return { position: remembered };
        }),
    }),
    {
      name: "slumbr:dock",
      partialize: (s) => ({
        enabled: s.enabled,
        order: s.order,
        position: s.position,
        smartPosition: s.smartPosition,
        appPositions: s.appPositions,
      }),
    },
  ),
);

/** Is a non-contextual widget enabled? Defaults to on when unset. */
export function isWidgetEnabled(enabled: Record<string, boolean>, id: string): boolean {
  return enabled[id] !== false;
}
